// Firebase Authentication for File Upload API
// This file now uses the backend API with Firebase authentication instead of direct AWS credentials

// API endpoint configuration
const API_CONFIG = {
  uploadEndpoint: 'https://gottea-search-5gtrn.ondigitalocean.app/files/upload',
  cdnDomain: process.env.REACT_APP_CLOUDFRONT_DOMAIN,
};

// File size limits in bytes
const MAX_IMAGE_SIZE = 4 * 1024 * 1024; // 4MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 10MB

// Helper function to get Firebase ID token
const getFirebaseIdToken = async () => {
  try {
    // Import Firebase dynamically to avoid issues if Firebase isn't initialized yet
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated. Please sign in to upload files.');
    }
    
    return await user.getIdToken();
  } catch (error) {
    console.error('Error getting Firebase ID token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
};

// Function to validate file size
const validateFileSize = (file) => {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  
  if (isImage && file.size > MAX_IMAGE_SIZE) {
    throw new Error(`Image size must be less than 4MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
  }
  
  if (isVideo && file.size > MAX_VIDEO_SIZE) {
    throw new Error(`Video size must be less than 10MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
  }
  
  if (!isImage && !isVideo) {
    throw new Error('File must be either an image or video');
  }
};

// Function to validate video dimensions
const validateVideoDimensions = (file) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('video/')) {
      resolve(); // Not a video, skip validation
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      
      if (width !== 1920 || height !== 1080) {
        reject(new Error(`Video must be 1920x1080 resolution. Current dimensions: ${width}x${height}. Please upload a 1920x1080 video only.`));
      } else {
        resolve();
      }
    };
    
    video.onerror = () => {
      reject(new Error('Unable to read video dimensions. Please ensure the video file is valid.'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

// Function to upload file using Firebase authenticated API
export const uploadToS3 = async (file, folder = 'menuItems', onProgress) => {
  // Validate file size before proceeding
  validateFileSize(file);
  
  // Validate video dimensions if it's a video file
  if (file.type.startsWith('video/')) {
    try {
      await validateVideoDimensions(file);
    } catch (error) {
      throw error;
    }
  }
  
  try {
    // Get Firebase ID token for authentication
    const idToken = await getFirebaseIdToken();
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    
    // Simulate progress for API call
    if (onProgress) onProgress(10, 'Preparing upload...');
    
    // Make authenticated API request
    const response = await fetch(API_CONFIG.uploadEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        ...(window.localStorage.getItem('gottea_admin_session_id') ? { 'X-Admin-Session-Id': window.localStorage.getItem('gottea_admin_session_id') } : {})
      },
      body: formData
    });
    
    if (onProgress) onProgress(50, 'Uploading to server...');
    
    // Handle response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle specific authentication errors
      if (response.status === 401) {
        if (errorData.error?.includes('expired')) {
          throw new Error('Authentication expired. Please sign in again.');
        } else if (errorData.error?.includes('required')) {
          throw new Error('Authentication required. Please sign in to upload files.');
        } else {
          throw new Error('Authentication failed. Please sign in again.');
        }
      }
      
      // Handle other errors
      if (response.status === 403) {
        throw new Error('Permission denied. You may not have permission to upload files.');
      }
      
      if (response.status === 413) {
        throw new Error('File too large. Please check file size limits.');
      }
      
      throw new Error(errorData.error || `Upload failed with status ${response.status}`);
    }
    
    const result = await response.json();
    
    if (onProgress) onProgress(100, 'Upload completed!');
    
    // Return the CDN URL from the API response
    return result.cdnUrl;
    
  } catch (error) {
    console.error('Error uploading file:', error);
    
    // Re-throw authentication errors for proper handling
    if (error.message.includes('Authentication') || 
        error.message.includes('sign in') ||
        error.message.includes('expired')) {
      throw error;
    }
    
    // Handle network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    
    // Handle other errors
    throw new Error(`Upload failed: ${error.message}`);
  }
};

// Export configuration for backward compatibility
export const s3Config = {
  bucketName: 'legacy-support', // No longer used directly
  cdnDomain: API_CONFIG.cdnDomain,
};
